/**
 * Navigation Icons
 * Icons used for navigation, menus, routes, and directional indicators
 */

import React from 'react'
import { SvgIcon, SvgIconProps } from '@mui/joy'

/**
 * Dollar icon for monetary values
 */
export const DollarIcon: React.FC<SvgIconProps> = (props) => (
    <SvgIcon sx={{ width: 16, height: 16 }} {...props}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <g clipPath="url(#clip0_394_4808)">
                <path
                    d="M14.6663 7.99967C14.6663 11.6817 11.6817 14.6663 7.99967 14.6663C4.31767 14.6663 1.33301 11.6817 1.33301 7.99967C1.33301 4.31767 4.31767 1.33301 7.99967 1.33301C11.6817 1.33301 14.6663 4.31767 14.6663 7.99967Z"
                    stroke="#8C9095"
                />
                <path
                    d="M9.80611 6.70704C9.74011 6.19837 9.15611 5.37704 8.10611 5.37704C6.88678 5.37704 6.37345 6.05237 6.26945 6.39037C6.10678 6.84237 6.13945 7.77104 7.56945 7.87237C9.35611 7.99904 10.0721 8.20971 9.98145 9.30371C9.89078 10.3977 8.89411 10.6337 8.10678 10.6084C7.31945 10.583 6.03145 10.2217 5.98145 9.24837M7.98145 4.66504V5.37971M7.98145 10.6017V11.3317"
                    stroke="#8C9095"
                    strokeLinecap="round"
                />
            </g>
            <defs>
                <clipPath id="clip0_394_4808">
                    <rect width="16" height="16" fill="white" />
                </clipPath>
            </defs>
        </svg>
    </SvgIcon>
)

/**
 * Route icon showing origin and destination points
 */
export const RouteIcon: React.FC = () => (
    <svg width="8" height="40" viewBox="0 0 8 40" fill="none">
        <line x1="4" y1="3" x2="4" y2="37" stroke="#363739" strokeWidth="1.5" strokeDasharray="2 2" />
        <circle cx="4" cy="4" r="3" fill="white" stroke="#363739" strokeWidth="1.5" />
        <circle cx="4" cy="36" r="3" fill="#363739" />
    </svg>
)

/**
 * Arrow icon for navigation between locations
 */
export const ArrowDownIcon: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 4L10 16M10 16L14 12M10 16L6 12" stroke="#363739" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

/**
 * Menu dots vertical icon for table row actions
 */
export const MenuDotsIcon: React.FC<SvgIconProps> = (props) => (
    <SvgIcon sx={{ width: 20, height: 20 }} {...props}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.99953 5.22155C10.4078 5.22155 10.7381 4.89051 10.7381 4.48293C10.7381 4.07465 10.4071 3.74431 9.99953 3.74431C9.59125 3.74431 9.26091 4.07534 9.26091 4.48293C9.26091 4.8912 9.59194 5.22155 9.99953 5.22155ZM9.99953 6.20707C10.9513 6.20707 11.7237 5.43465 11.7237 4.48293C11.7237 3.5312 10.9513 2.75879 9.99953 2.75879C9.0478 2.75879 8.27539 3.5312 8.27539 4.48293C8.27539 5.43465 9.0478 6.20707 9.99953 6.20707Z"
                fill="#192129"
            />
            <path
                d="M10.0004 16.8963C10.7622 16.8963 11.3797 16.2788 11.3797 15.517C11.3797 14.7552 10.7622 14.1377 10.0004 14.1377C9.23863 14.1377 8.62109 14.7552 8.62109 15.517C8.62109 16.2788 9.23863 16.8963 10.0004 16.8963Z"
                fill="#192129"
                stroke="#192129"
                strokeWidth="0.827586"
            />
            <path
                d="M9.65527 6.89648V7.53304H10.3449V6.89648H9.65527ZM9.65527 8.06338V8.69993H10.3449V8.06338H9.65527ZM9.65527 9.23097V9.86752H10.3449V9.23097H9.65527ZM9.65527 10.3979V11.0344H10.3449V10.3979H9.65527Z"
                fill="#636D79"
            />
            <path d="M11.7237 11.4294L11.3168 11.0342L9.99953 12.3135L8.68229 11.0342L8.27539 11.4294L9.99953 13.1031L11.7237 11.4294Z" fill="#636D79" />
        </svg>
    </SvgIcon>
)

/**
 * Menu dots icon - used in tabs before origin/destination (20x20)
 */
export const TabMenuIcon: React.FC<SvgIconProps> = (props) => (
    <SvgIcon sx={{ width: 20, height: 20 }} {...props}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.99953 5.22155C10.4078 5.22155 10.7381 4.89051 10.7381 4.48293C10.7381 4.07465 10.4071 3.74431 9.99953 3.74431C9.59125 3.74431 9.26091 4.07534 9.26091 4.48293C9.26091 4.8912 9.59194 5.22155 9.99953 5.22155ZM9.99953 6.20707C10.9513 6.20707 11.7237 5.43465 11.7237 4.48293C11.7237 3.5312 10.9513 2.75879 9.99953 2.75879C9.0478 2.75879 8.27539 3.5312 8.27539 4.48293C8.27539 5.43465 9.0478 6.20707 9.99953 6.20707Z"
                fill="#192129"
            />
            <path
                d="M10.0004 16.8963C10.7622 16.8963 11.3797 16.2788 11.3797 15.517C11.3797 14.7552 10.7622 14.1377 10.0004 14.1377C9.23863 14.1377 8.62109 14.7552 8.62109 15.517C8.62109 16.2788 9.23863 16.8963 10.0004 16.8963Z"
                fill="#192129"
                stroke="#192129"
                strokeWidth="0.827586"
            />
            <path
                d="M9.65527 6.89648V7.53304H10.3449V6.89648H9.65527ZM9.65527 8.06338V8.69993H10.3449V8.06338H9.65527ZM9.65527 9.23097V9.86752H10.3449V9.23097H9.65527ZM9.65527 10.3979V11.0344H10.3449V10.3979H9.65527Z"
                fill="#636D79"
            />
            <path d="M11.7237 11.4294L11.3168 11.0342L9.99953 12.3135L8.68229 11.0342L8.27539 11.4294L9.99953 13.1031L11.7237 11.4294Z" fill="#636D79" />
        </svg>
    </SvgIcon>
)

/**
 * Dashboard home icon - used in sidebar for LoadOps dashboard (22x21)
 */
export const DashboardHomeIcon: React.FC<SvgIconProps> = (props) => (
    <SvgIcon sx={{ width: 22, height: 21 }} {...props}>
        <svg width="22" height="21" viewBox="0 0 22 21" fill="none">
            <g clipPath="url(#clip0_dashboard_home)">
                <path
                    d="M11.3868 1.03459L19.7936 7.65804C20.0087 7.82727 20.1657 8.05922 20.2426 8.32163C20.3195 8.58404 20.3126 8.86385 20.2227 9.12213C20.1328 9.38043 19.9646 9.60432 19.7414 9.76267C19.5181 9.92111 19.2509 10.006 18.977 10.0057H18.3383V13.4224C18.3383 16.1831 18.3383 17.5644 17.4787 18.4215C16.62 19.2796 15.2362 19.2796 12.4705 19.2796H8.55853C5.7928 19.2796 4.40896 19.2796 3.55029 18.4215C2.69064 17.5644 2.69064 16.1831 2.69064 13.4224V10.0057H2.05202C1.77811 10.006 1.51092 9.92111 1.28765 9.76267C1.06438 9.60432 0.89611 9.38043 0.806271 9.12213C0.716431 8.86385 0.709486 8.58404 0.786399 8.32163C0.863313 8.05922 1.02026 7.82727 1.23541 7.65804L9.64214 1.03459C9.89063 0.838871 10.1979 0.732422 10.5145 0.732422C10.8311 0.732422 11.1383 0.838871 11.3868 1.03459Z"
                    stroke="currentColor"
                    strokeWidth="1.46563"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M12.9592 19.2796V14.8868C12.9592 13.974 12.9592 13.5181 12.7627 13.1784C12.634 12.9559 12.4487 12.7711 12.2257 12.6425C11.8854 12.4463 11.4287 12.4463 10.5143 12.4463C9.59988 12.4463 9.14316 12.4463 8.80282 12.6425C8.57983 12.7711 8.39466 12.9559 8.26591 13.1784C8.06934 13.5181 8.06934 13.974 8.06934 14.8868V19.2796"
                    stroke="currentColor"
                    strokeWidth="1.46563"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </g>
            <defs>
                <clipPath id="clip0_dashboard_home">
                    <rect width="21.5156" height="20.5" fill="white" />
                </clipPath>
            </defs>
        </svg>
    </SvgIcon>
)

/**
 * Package layers icon - used in tabs before load count (16x16)
 */
export const PackageLayersIcon: React.FC<SvgIconProps> = (props) => (
    <SvgIcon sx={{ width: 16, height: 16 }} {...props}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
                d="M8 14.6663C7.45467 14.6663 6.93333 14.4463 5.89133 14.0063C3.29733 12.9103 2 12.3617 2 11.4397V4.66634M8 14.6663C8.54533 14.6663 9.06667 14.4463 10.1087 14.0063C12.7027 12.9103 14 12.3617 14 11.4397V4.66634M8 14.6663V7.56967M4 7.99967L5.33333 8.66634M11.3333 2.66634L4.66667 5.99967M5.55067 6.46034L3.60333 5.51834C2.53467 5.00101 2 4.74234 2 4.33301C2 3.92367 2.53467 3.66501 3.60333 3.14767L5.55 2.20567C6.75333 1.62367 7.35333 1.33301 8 1.33301C8.64667 1.33301 9.24733 1.62367 10.4493 2.20567L12.3967 3.14767C13.4653 3.66501 14 3.92367 14 4.33301C14 4.74234 13.4653 5.00101 12.3967 5.51834L10.45 6.46034C9.24667 7.04234 8.64667 7.33301 8 7.33301C7.35333 7.33301 6.75267 7.04234 5.55067 6.46034Z"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </SvgIcon>
)

/**
 * Map icon for opening Google Maps directions
 */
interface MapIconProps {
    size?: number
    color?: string
}

export const MapIcon = ({ size = 24, color = '#0046E0' }: MapIconProps = {}) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 22" width={size} height={size} fill="none">
        <path
            d="M11.0495 1.83331C7.50198 1.83331 4.63281 4.70248 4.63281 8.24998C4.63281 13.0625 11.0495 20.1666 11.0495 20.1666C11.0495 20.1666 17.4661 13.0625 17.4661 8.24998C17.4661 4.70248 14.597 1.83331 11.0495 1.83331ZM11.0495 10.5416C9.78448 10.5416 8.75781 9.51498 8.75781 8.24998C8.75781 6.98498 9.78448 5.95831 11.0495 5.95831C12.3145 5.95831 13.3411 6.98498 13.3411 8.24998C13.3411 9.51498 12.3145 10.5416 11.0495 10.5416Z"
            fill={color}
        />
    </svg>
)
